


























































































































import {
  getIresDeviceCategoryFromSerialNumber,
  IresDeviceCategory,
} from "@/data/models/IresDevice";
import MenuIconWithSelectionListComponent from "@/components/common/FilterMenu.vue";
import { RunOverview } from "@/data/models/RunOverview";
import {
  RawSampleData,
  loadRun,
  loadSamplesOfRun,
} from "@/data/repository/RunRepository";

import router from "@/router/router";
import * as base64Buffer from "base64-arraybuffer";

import { ToolbarContentModule } from "@/store/modules/ToolbarContent";
import { formatFullJsDate } from "@/utils/DateFormatter";
import { advanceByMinutes } from "@/utils/DateHelper";
import { IresDeviceResponseReader } from "@/utils/IresDeviceResponseReader";
import { Component, Vue, Watch } from "vue-property-decorator";
import {
  filterComBoxErrorFlagsIfNeeded,
  formatActiveDryingPauseState,
  getComBoxErrorFlagDescriptions,
  getComBoxErrorFlagsFromErrorState,
  getDryingPauseTypeFromFlag,
} from "@/data/models/ComBox";
import {
  ComBoxSensorType,
  formatSensorType as formatHardwareSensorType,
  formatTemperature,
  getSensorData,
  isBleResistanceSensorType,
  ROOM_SENSOR_INDEX,
} from "@/data/models/ComBoxSensorData";
import { getPanelTypeFromSerialNumber, PanelType } from "@/data/models/Panel";
import { hexToUint8Array } from "@/utils/ByteUtils";
import { getActiveUser } from "@/data/repository/UserRepository";
import { markEndDateManualCorrectionsAccess } from "@/permissions/UserPermissions";
import { filterOutUndefined } from "@/utils/Utils";
import { getSensorDataStartIndexFromSampleFormatVersion, hasV2OrGreaterSampleData, isGuideSensorSet } from "@/data/models/SampleInfo";
import { calculateSlotNumberFromIndex, formatSensorSlotLabel, getSerialForSensorIndex, isHyDryMiniSerial } from "@/data/models/ComBox";
import {
  COLOR_SENSOR_USAGE_MEASUREMENT,
  COLOR_SENSOR_USAGE_PROGRAM,
  COLOR_SENSOR_USAGE_REFERENCE,
  COLOR_STANDARD_WHITE,
  COLOR_WHITE_BACKGROUND_DARK,
} from "@/utils/colors";
import { themed } from "@/utils/Theme";
import {
  getComBoxSampleSensorColumnKey,
  getVisibleComBoxSampleSensorIndexes,
} from "@/utils/devices/SampleSensorColumns";
import ToolbarOverflowItemComponent from "@/components/common/ToolbarOverflowItem.vue";

interface SampleInfo {
  sampleIndex: number;
  event: number;
  step: number;
  cycle: number;
}
interface ComBoxSampleInfo extends SampleInfo {
  status: string;
  time: string;
  power: string;
  guideSensorIndex: number;

  roomSensor: string;
  floorSensor0: string;
  floorSensor1: string;
  floorSensor2: string;
  floorSensor3: string;

  // ==================== FIRMWARE v4.0.0+ CHANGES ====================
  floorSensor5?: string;
  floorSensor6?: string;
  floorSensor7?: string;
  floorSensor8?: string;

  isSpecialEvent?: boolean;
}

interface PanelSampleInfo extends SampleInfo {
  time: string;
  power: number;
  status: number;
  eventString: string;

  wallTemp1: number;
  wallTemp2?: number;

  chipTemp1: number;
  chipTemp2?: number;

  wallTemp1Max?: string;
  wallTemp2Max?: string;
  heatRatio?: string;

  isForIresClientOrAdmin?: boolean;
}

enum SampleFilter {
  ALL,
  EVENT,
  DRYING_EVENT,
  BATTERY_EVENT,
  POWER_REDUCTION,
  MONITOR,
  END_OF_STEP,
}

@Component({
  components: {
    "toolbar-overflow-item": ToolbarOverflowItemComponent,
    "menu-icon": MenuIconWithSelectionListComponent,
  },
})
export default class IresDeviceSampleListComponent extends Vue {
  serialNumber = "";
  runOverview!: RunOverview;
  didLoadRunDetails = false;

  isLoading = false;

  page = 1;

  iresDeviceCategory = IresDeviceCategory.PANEL;
  panelType = PanelType.HYDRY;

  selectedFilter = SampleFilter.ALL;
  doesSupportExtendedSensors = false;
  visibleComBoxSensorIndexes: number[] = [];

  get activeUser() {
    return getActiveUser();
  }

  get isAllowedToMarkManuallyCorrectedData() {
    return markEndDateManualCorrectionsAccess(this.activeUser);
  }

  get hasGroundTemp() {
    return this.panelType == PanelType.EDGE;
  }

  get sampleFilterIconRes() {
    return "mdi-filter-variant";
  }

  /**
   * Phones (below 600 px): the samples stay a table that scrolls sideways (`mobile-breakpoint` 0;
   * Vuetify's own mobile layout turned every sample into a card of label rows), dense, with the
   * time column pinned.
   */
  get isPhone() {
    return this.$vuetify.breakpoint.xsOnly;
  }

  /** The pinned time column needs the table's own colour, or the values scroll visibly beneath it. */
  get phoneTableStyle() {
    return { "--pinned-background": themed(COLOR_STANDARD_WHITE, COLOR_WHITE_BACKGROUND_DARK) };
  }

  get footerProps() {
    return {
      showCurrentPage: true,
      pageText: "",
      itemsPerPageText: this.$t("sampleList.samplesPerPage").toString(),
      itemsPerPageOptions: [200, 500],
    };
  }

  get filterOptions() {
    switch (this.iresDeviceCategory) {
      case IresDeviceCategory.PANEL:
        return [
          SampleFilter.ALL,
          SampleFilter.EVENT,
          SampleFilter.DRYING_EVENT,
          SampleFilter.BATTERY_EVENT,
          SampleFilter.POWER_REDUCTION,
        ];
      case IresDeviceCategory.COMBOX:
        return [
          SampleFilter.ALL,
          SampleFilter.EVENT,
          SampleFilter.BATTERY_EVENT,
          SampleFilter.MONITOR,
          SampleFilter.END_OF_STEP,
        ];
    }

    return [];
  }

  getFilterOptionName(filter: SampleFilter): string {
    switch (filter) {
      case SampleFilter.ALL:
        return this.$t("sampleList.filterAll").toString();
      case SampleFilter.EVENT:
        return this.$t("sampleList.filterEvent").toString();
      case SampleFilter.DRYING_EVENT:
        return this.$t("sampleList.filterDryingEvent").toString();
      case SampleFilter.BATTERY_EVENT:
        return this.$t("sampleList.filterBatteryEvent").toString();
      case SampleFilter.POWER_REDUCTION:
        return this.$t("sampleList.filterPowerReduction").toString();
      case SampleFilter.MONITOR:
        return this.$t("sampleList.filterMonitor").toString();
      case SampleFilter.END_OF_STEP:
        return this.$t("sampleList.filterEndOfStep").toString();
    }
  }

  applyFilter(filter: SampleFilter, sample: SampleInfo): boolean {
    const event = sample.event;
    switch (filter) {
      case SampleFilter.ALL:
        return true;
      case SampleFilter.EVENT:
        return event !== 0;
      case SampleFilter.END_OF_STEP:
        return event === 15;
      case SampleFilter.DRYING_EVENT:
        return event !== 0 && event !== 255 && !(event >= 30 && event <= 40);
      case SampleFilter.BATTERY_EVENT:
        return event == 70 || event == 90 || event == 91;
      case SampleFilter.POWER_REDUCTION:
        return event == 71;
      case SampleFilter.MONITOR:
        return event == 22;
    }
  }

  sampleInfo: SampleInfo[] = [];

  tableHeight = 200;

  showSamplesDescending = false;

  get sampleInfoToDisplay() {
    this.isLoading = true;
    const filter = this.selectedFilter;
    const sampleInfo: SampleInfo[] = this.sampleInfo;

    const sorted = this.showSamplesDescending
      ? sampleInfo.slice().reverse()
      : sampleInfo;

    const filtered = sorted.filter((sample: SampleInfo) =>
      this.applyFilter(filter, sample)
    );

    this.isLoading = false;
    this.page = 1;

    return filtered;
  }

  get showComBoxInfo() {
    return this.iresDeviceCategory === IresDeviceCategory.COMBOX;
  }

  get showPanelInfo() {
    return this.iresDeviceCategory === IresDeviceCategory.PANEL;
  }

  get referenceSensorIndex() {
    return this.runOverview.sensorUsage?.findIndex(
      (usage) => usage === "REFERENCE"
    );
  }

  getComBoxSensorDataStyle(guideSensorIndex: number, sensorIndex: number, isSpecialEvent = false) {
    if (isSpecialEvent) {
      return `font-weight: normal`;
    }

    if (sensorIndex === this.referenceSensorIndex) {
      return "font-style: italic;";
    }

    return `font-weight: ${
      guideSensorIndex === sensorIndex && isGuideSensorSet(guideSensorIndex) ? "bold" : "normal"
    }`;
  }

  @Watch("selectedFilter")
  updateToolbarTitle() {
    const run = this.runOverview;

    if (!run) {
      return;
    }

    const toolbarTitle = this.$t("sampleList.formatTitle", [
      this.serialNumber,
      run.chainNumber,
      run.programRunID,
      run.programNumber ?? 0,
      formatFullJsDate(run.startDate.toDate()),
    ]).toString();

    const filter = this.selectedFilter;
    ToolbarContentModule.setToolbarTitle(
      filter == SampleFilter.ALL
        ? toolbarTitle
        : `${toolbarTitle} (${this.getFilterOptionName(filter)})`
    );
  }

  get headers() {
    if (this.iresDeviceCategory === IresDeviceCategory.PANEL) {
      const isEdgePanel = this.panelType == PanelType.EDGE;
      return [
        {
          width: "20%",
          value: "time",
          sortable: false,
          text: this.$t("sampleList.titleTime").toString(),
        },
        {
          value: "sampleIndex",
          sortable: false,
          text: this.$t("sampleList.titleIndex").toString(),
        },
        {
          value: "power",
          sortable: false,
          text: this.$t("sampleList.titlePower").toString(),
        },
        {
          value: "step",
          sortable: false,
          text: this.$t("sampleList.titleStep").toString(),
        },
        {
          value: "cycle",
          sortable: false,
          text: this.$t("sampleList.titleCycle").toString(),
        },
        {
          value: "status",
          sortable: false,
          text: this.$t("sampleList.titleStatus").toString(),
        },
        {
          value: "eventString",
          sortable: false,
          text: this.$t("sampleList.titleEvent").toString(),
        },
        {
          value: "wallTemp1",
          sortable: false,
          text: this.$t("sampleList.titleWallTemp1").toString(),
        },
        {
          value: "chipTemp1",
          sortable: false,
          text: this.$t("sampleList.titleChipTemp1").toString(),
        },
        ...(isEdgePanel
          ? [
              {
                value: "wallTemp2",
                sortable: false,
                text: this.$t("sampleList.titleWallTemp2").toString(),
              },
              {
                value: "chipTemp2",
                sortable: false,
                text: this.$t("sampleList.titleChipTemp2").toString(),
              },
            ]
          : []),
        {
          value: "wallTemp1Max",
          sortable: false,
          text: this.$t("sampleList.titleWallTemp1Max").toString(),
        },
        {
          value: "wallTemp2Max",
          sortable: false,
          text: this.$t(
            isEdgePanel
              ? "sampleList.titleWallChip1Max"
              : "sampleList.titleWallTemp2Max"
          ).toString(),
        },
        {
          value: "heatRatio",
          sortable: false,
          text: this.$t("sampleList.heatRatio").toString(),
        },
      ];
    } else {
      return [
        {
          width: "20%",
          value: "time",
          sortable: false,
          text: this.$t("sampleList.titleTime").toString(),
        },
        {
          value: "sampleIndex",
          sortable: false,
          text: this.$t("sampleList.titleIndex").toString(),
        },
        {
          value: "power",
          sortable: false,
          text: this.$t("sampleList.titlePower").toString(),
        },
        {
          value: "status",
          sortable: false,
          text: this.$t("sampleList.titleStatus").toString(),
        },
        {
          value: "event",
          sortable: false,
          text: this.$t("sampleList.titleEvent").toString(),
        },
        {
          value: "step",
          sortable: false,
          text: this.$t("sampleList.titleStep").toString(),
        },
        {
          value: "cycle",
          sortable: false,
          text: this.$t("sampleList.titleCycle").toString(),
        },
        ...this.visibleComBoxSensorIndexes
          .map((sensorIndex) => ({
            value: getComBoxSampleSensorColumnKey(sensorIndex),
            sortable: false,
            text: this.formatSensorType(sensorIndex),
          })),
      ];
    }
  }

  mounted() {
    this.onWindowsResize();
  }

  async created() {
    this.serialNumber = router.currentRoute.query["serialNumber"] as string;
    const runId = router.currentRoute.query["runId"] as string;
    // ==================== FIRMWARE v4.0.0+ CHANGES ====================
    this.doesSupportExtendedSensors = router.currentRoute.query["doesSupportExtendedSensors"] === "true";

    this.isLoading = true;
    const run = await loadRun(this.serialNumber, runId);

    if (!run) {
      this.isLoading = false;
      router.back();
      return;
    }

    this.iresDeviceCategory = getIresDeviceCategoryFromSerialNumber(
      this.serialNumber
    );

    this.runOverview = run;

    this.updateToolbarTitle();

    const rawSampleData = await loadSamplesOfRun(this.serialNumber, runId);

    this.displaySampleData(this.serialNumber, run, rawSampleData);
    this.isLoading = false;
  }

  formatPercent(percent?: number, maximumFractionDigits?: number) {
    if (percent === undefined) {
      return "-";
    }

    return this.$t("common.formatPercent", [
      percent.toLocaleString(undefined, {
        maximumFractionDigits: maximumFractionDigits,
      }),
    ]).toString();
  }

  formatExponentAsSuperscript(exponent: number): string {
    const superscriptDigits: Record<string, string> = {
      "0": "⁰",
      "1": "¹",
      "2": "²",
      "3": "³",
      "4": "⁴",
      "5": "⁵",
      "6": "⁶",
      "7": "⁷",
      "8": "⁸",
      "9": "⁹",
      "-": "⁻",
    };

    return exponent
      .toString()
      .split("")
      .map((char) => superscriptDigits[char] ?? char)
      .join("");
  }

  formatSampleResistanceValue(codedResistance?: number, resistance?: number) {
    if (codedResistance !== undefined) {
      const exp = codedResistance & 0x0f;
      const bcdMantissa = codedResistance >>> 4;
      const hundreds = (bcdMantissa >>> 8) & 0x0f;
      const tens = (bcdMantissa >>> 4) & 0x0f;
      const ones = bcdMantissa & 0x0f;
      const hasValidBcdMantissa = hundreds <= 9 && tens <= 9 && ones <= 9;

      if (exp <= 0x0c && hasValidBcdMantissa) {
        const mantissa = hundreds * 100 + tens * 10 + ones;
        return `${mantissa} x 10${this.formatExponentAsSuperscript(exp)} Ω`;
      }
    }

    if (resistance === undefined) {
      return "-";
    }

    return `${resistance.toLocaleString()} Ω`;
  }

  displaySampleData(
    serialNumber: string,
    runOverview: RunOverview,
    rawSampleData: RawSampleData[]
  ) {
    if (this.iresDeviceCategory == IresDeviceCategory.COMBOX) {
      this.displaySampleDataOfComBox(runOverview, rawSampleData);
    } else if (this.iresDeviceCategory === IresDeviceCategory.PANEL) {
      this.displaySampleDataOfPanel(serialNumber, runOverview, rawSampleData);
    }
  }

  formatSensorType(sensorIndex: number) {
    const sensorType = this.runOverview?.sensorConfig?.[sensorIndex];
    if (sensorType && sensorType !== ComBoxSensorType.EMPTY && sensorType !== ComBoxSensorType.UNKNOWN) {
      return formatHardwareSensorType(this, sensorType);
    }
    const slotLabel = formatSensorSlotLabel(sensorIndex, sensorType, getSerialForSensorIndex(this.runOverview?.sensorSlotSerials, sensorIndex), this.serialNumber ? isHyDryMiniSerial(this.serialNumber) : false) ?? `${calculateSlotNumberFromIndex(sensorIndex)}L`;
    return sensorIndex === ROOM_SENSOR_INDEX
      ? this.$t("iresDeviceSensor.roomSensor").toString()
      : `Sensor${slotLabel}`;
  }

  getSensorUsageColor(sensorIndex: number): string | null {
    const sensorUsage = this.runOverview.sensorUsage?.[sensorIndex];
    switch (sensorUsage) {
      case "MEASUREMENT": return COLOR_SENSOR_USAGE_MEASUREMENT;
      case "PROGRAM": return COLOR_SENSOR_USAGE_PROGRAM;
      case "REFERENCE": return COLOR_SENSOR_USAGE_REFERENCE;
      default: return null;
    }
  }

  formatSensorUsageShort(sensorUsage?: string): string | null {
    switch (sensorUsage) {
      case "PROGRAM":
        return this.$t("comBoxDetail.sensorUsage.programShort").toString();
      case "MEASUREMENT":
        return this.$t("comBoxDetail.sensorUsage.measurementShort").toString();
      case "REFERENCE":
        return this.$t("comBoxDetail.sensorUsage.referenceShort").toString();
      default:
        return null;
    }
  }

  undefinedIf255(value: number) {
    return value == 255 ? undefined : value;
  }

  getPanelUnit16Value(iresDeviceResponseReader: IresDeviceResponseReader) {
    const uint16 =
      iresDeviceResponseReader.data[iresDeviceResponseReader.currentIndex] +
      iresDeviceResponseReader.data[iresDeviceResponseReader.currentIndex + 1] *
        256;

    iresDeviceResponseReader.skip(2);

    return uint16;
  }

  formatTimeSum(timeSum: number) {
    const days = Math.floor(timeSum / (60 * 24));
    const hours = Math.floor(timeSum / 60) - 24 * days;

    const minutes = timeSum % 60;
    const minutesString =
      minutes === 0 ? "00" : minutes < 10 ? `0${minutes}` : minutes.toString();

    if (days !== 0) {
      return `${days}:${hours}:${minutesString}`;
    } else {
      return `${hours}:${minutesString}`;
    }
  }

  displaySampleDataOfPanel(
    serialNumber: string,
    runOverview: RunOverview,
    rawSampleData: RawSampleData[]
  ) {
    const iresDeviceResponseReader = new IresDeviceResponseReader();
    this.panelType = getPanelTypeFromSerialNumber(serialNumber);

    const panelSampleInfoUpdate: PanelSampleInfo[] = [];

    const isEdgePanel = this.panelType == PanelType.EDGE;
    const date = runOverview.startDate.toDate();

    let previousSampleIndex = -1;
    let previousTimeDiffInMin = 0;

    let timeDiffToBeAdjusted = 0;

    rawSampleData.forEach((rawSampleData) => {
      const data = hexToUint8Array(rawSampleData.encodedSample);

      iresDeviceResponseReader.setNewData(data);

      const timeDiffInMinutes_ = this.getPanelUnit16Value(
        iresDeviceResponseReader
      );

      const wallTemp1 = iresDeviceResponseReader.readUint8();
      const chipTemp1 = iresDeviceResponseReader.readUint8();
      const power = this.getPanelUnit16Value(iresDeviceResponseReader);

      const stepAndProgram = iresDeviceResponseReader.readUint8();
      const step = stepAndProgram & 0x1f & 0xff;

      const cycleAndStatus = iresDeviceResponseReader.readUint8();
      const status = cycleAndStatus & 0x0f & 0xff;
      const cycle = ((cycleAndStatus & 0xf0) >>> 4) & 0xff;

      const event = iresDeviceResponseReader.readUint8();
      const sampleIndex = this.getPanelUnit16Value(iresDeviceResponseReader);

      // To temporarily fix the issue with the battery management for the panels (HDxxx series)
      if (sampleIndex === 0) {
        timeDiffToBeAdjusted = timeDiffInMinutes_;
      }

      const timeDiffInMinutes = timeDiffInMinutes_ - timeDiffToBeAdjusted;

      let wallTemp2: number | undefined = undefined;
      let chipTemp2: number | undefined = undefined;

      if (isEdgePanel) {
        wallTemp2 = iresDeviceResponseReader.readUint8();
        chipTemp2 = iresDeviceResponseReader.readUint8();
      }

      const heatRatio = iresDeviceResponseReader.readUint8();
      let heatRatioString = "";

      const isBatteryVoltageMeasurementEvent =
        event === 70 || event === 90 || event === 91;

      const isPowerReductionEvent = event === 71;
      const isDryingPauseInfoEvent = event === 27;

      if (event == 18) {
        heatRatioString = this.formatPercent(
          Math.floor((100 * heatRatio) / 255)
        );
      } else if (isBatteryVoltageMeasurementEvent) {
        const batteryVoltage = 10 * heatRatio + 2000;

        heatRatioString = this.$t("sampleList.formatVoltage", [
          batteryVoltage.toLocaleString(undefined, {
            maximumFractionDigits: 3,
          }),
        ]).toString();
      } else if (isPowerReductionEvent || isDryingPauseInfoEvent) {
        heatRatioString = heatRatio?.toString() ?? "";
      }

      const wallTemp1Max = this.undefinedIf255(
        iresDeviceResponseReader.readUint8()
      )?.toString();

      const wallTemp2Max = this.undefinedIf255(
        iresDeviceResponseReader.readUint8()
      )?.toString();

      advanceByMinutes(date, timeDiffInMinutes - previousTimeDiffInMin);

      const skipSampleForClients = previousTimeDiffInMin > timeDiffInMinutes;

      previousTimeDiffInMin = timeDiffInMinutes;

      const timeString_ = this.isAllowedToMarkManuallyCorrectedData && timeDiffToBeAdjusted !== 0
        ? `${formatFullJsDate(date)} (${this.formatTimeSum(
            timeDiffInMinutes
          )}*)`
        : `${formatFullJsDate(date)} (${this.formatTimeSum(
            timeDiffInMinutes
          )})`

      const timeString = this.isAllowedToMarkManuallyCorrectedData && skipSampleForClients
        ? "*" + timeString_
        : timeString_

      if (sampleIndex != previousSampleIndex) {
        if (!skipSampleForClients || this.isAllowedToMarkManuallyCorrectedData) {
          panelSampleInfoUpdate.push({
            sampleIndex: sampleIndex,
            time: timeString,
            wallTemp1: wallTemp1,
            chipTemp1: chipTemp1,
            power: power,
            step: step,
            cycle: cycle,
            status: status,
            event: event,
            eventString: event === 0 ? "" : event.toString(),
            wallTemp2: wallTemp2,
            chipTemp2: chipTemp2,
            heatRatio: heatRatioString,
            wallTemp1Max: isBatteryVoltageMeasurementEvent
              ? `${wallTemp1Max} (PCB)`
              : wallTemp1Max,
            wallTemp2Max: isPowerReductionEvent
              ? `${wallTemp2Max}W`
              : wallTemp2Max,
            isForIresClientOrAdmin: skipSampleForClients
          });
        }
      }

      previousSampleIndex = sampleIndex;
    });

    this.sampleInfo = panelSampleInfoUpdate;
  }

  displaySampleDataOfComBox(
    runOverview: RunOverview,
    rawSampleData: RawSampleData[]
  ) {
    const sensorConfig = runOverview.sensorConfig;

    if (!sensorConfig) {
      return;
    }

    const iresDeviceResponseReader = new IresDeviceResponseReader();

    const comBoxSampleInfoUpdate: ComBoxSampleInfo[] = [];
    const date = runOverview.startDate.toDate();
    let previousTimeDiffInMin = 0;

    rawSampleData.forEach((rawSampleData) => {
      const data = new Uint8Array(
        base64Buffer.decode(rawSampleData.encodedSample)
      );

      iresDeviceResponseReader.setNewData(data);

      const sampleIndex = iresDeviceResponseReader.readUint16();
      const event = iresDeviceResponseReader.readUint8();

      const status = iresDeviceResponseReader.readUint8();

      let deviceState,
        relay1state,
        relay2state,
        guideSensorIndex,
        step = 0,
        cycle = 0;

      if (hasV2OrGreaterSampleData(runOverview)) {
        deviceState = status % 16;
        relay1state = (status >>> 4) % 4;
        relay2state = (status >>> 6) % 4;

        guideSensorIndex = iresDeviceResponseReader.readUint8();

        const programAndStep = iresDeviceResponseReader.readUint8();
        step = (programAndStep >>> 4) % 16;

        cycle = iresDeviceResponseReader.readUint8();
      } else {
        deviceState = status % 16;
        relay1state = (status >>> 4) % 2;
        relay2state = (status >>> 5) % 2;
        guideSensorIndex = status >>> 6;

        step = 0;
        cycle = 0;
      }

      const timeDiffInMin = iresDeviceResponseReader.readUint16();
      const runEnergyInWh = iresDeviceResponseReader.readShortMeterReading();

      const sensorValues: string[] = [];

      const isVoltageMeasurementSample = event === 70 || event === 71 || event === 72;
      const isWarningOrErrorEvent = event === 28 || event === 29;
      const isPowerFailInfoEvent = event === 76;
      const isDryingPauseInfoEvent = event === 27;
      const isUartStatusEvent = event === 74;
      const isConfigurationInfoEvent = event === 64; 
      const isConfigurationTypeInfoEvent = event === 67;

      const isSpecialEvent = isVoltageMeasurementSample ||
        isWarningOrErrorEvent ||
        isPowerFailInfoEvent ||
        isDryingPauseInfoEvent ||
        isUartStatusEvent ||
        isConfigurationInfoEvent ||
        isConfigurationTypeInfoEvent;

      if (isDryingPauseInfoEvent) {
        const dataLen = iresDeviceResponseReader.readUint8();

        if (dataLen === 3 || dataLen === 4) {
          const newPauseTypeFlag = iresDeviceResponseReader.readUint8();
          const newPauseType = getDryingPauseTypeFromFlag(newPauseTypeFlag);
          
          const lastPauseTypeFlag = iresDeviceResponseReader.readUint8();
          const lastPauseType = getDryingPauseTypeFromFlag(lastPauseTypeFlag);

          const formattedDryingPauseState = formatActiveDryingPauseState(this, newPauseType, lastPauseType);
          
          const pauseStateNumber = iresDeviceResponseReader.readUint8();
          const pauseState = pauseStateNumber && pauseStateNumber !== 255
            ? this.$t("sampleList.pauseState", [
                  pauseStateNumber
                  .toString(2).
                  padStart(8, "0")
                ]
              ).toString()
            : "";

          let event = "";
          if (dataLen === 4) {
            const eventNumber = iresDeviceResponseReader.readUint8();

            if (eventNumber && eventNumber !== 255) {
              event = this.$t("sampleList.event", [eventNumber]).toString();
            }
          }

          sensorValues.push(pauseState);
          sensorValues.push(event);
          sensorValues.push("");
          sensorValues.push("");
          sensorValues.push(formattedDryingPauseState);
        }
      }
      else if (isVoltageMeasurementSample) {
        const batteryVoltage = iresDeviceResponseReader.readUint16() / 1000;

        sensorValues.push("");
        sensorValues.push("");
        sensorValues.push("");
        sensorValues.push("");
        sensorValues.push(
          this.$t("sampleList.formatVoltage", [
            batteryVoltage.toLocaleString(undefined, {
              maximumFractionDigits: 3,
            }),
          ]).toString()
        );
      } else if (isWarningOrErrorEvent) {
        sensorValues.push("");
        sensorValues.push("");
        sensorValues.push("");
        sensorValues.push("");

        const errorState = iresDeviceResponseReader.readBytes(4);
        // ==================== FIRMWARE v4.0.0+ CHANGES ====================
        const errors = filterComBoxErrorFlagsIfNeeded(
          getComBoxErrorFlagsFromErrorState(errorState),
          this.doesSupportExtendedSensors
        );

        sensorValues.push(
          getComBoxErrorFlagDescriptions(this, errors, { separator: ", ", doesSupportExtendedSensors: this.doesSupportExtendedSensors }) ??
            ""
        );
      } else if (isConfigurationInfoEvent) {
        sensorValues.push("");
        sensorValues.push("");
        sensorValues.push("");
        sensorValues.push("");

        const configInfo: number[] = [];
        for (let i = 0; i < 4; i++) {
          configInfo.push(iresDeviceResponseReader.readUint8());
        }
        sensorValues.push(this.$t("comBoxSampleIotInfo.configurationInfo", [configInfo]).toString());
      }  else if (isConfigurationTypeInfoEvent) {
        sensorValues.push("");
        sensorValues.push("");
        sensorValues.push("");
        sensorValues.push("");

        sensorValues.push(this.$t("comBoxSampleIotInfo.configurationType", [iresDeviceResponseReader.readUint8()]).toString());
      } else if (isPowerFailInfoEvent) {
        sensorValues.push("");
        sensorValues.push("");
        sensorValues.push("");
        sensorValues.push("");

        const bc66DriverState = iresDeviceResponseReader.readUint8();
        const nb1DriverState = iresDeviceResponseReader.readUint8();
        const numPowerFailInfo = iresDeviceResponseReader.readUint8();
        const iotMessageFailureCause = iresDeviceResponseReader.readUint8();

        let iotMessageFailureDescriptionRes = "common.unknown";

        switch (iotMessageFailureCause) {
          case 0:
            iotMessageFailureDescriptionRes = "comBoxSampleIotInfo.success";
            break;
          case 1:
            iotMessageFailureDescriptionRes =
              "comBoxSampleIotInfo.batteryVoltageToLow";
            break;
          case 2:
            iotMessageFailureDescriptionRes = "comBoxSampleIotInfo.noNetwork";
            break;
          case 3:
            iotMessageFailureDescriptionRes = "comBoxSampleIotInfo.noIpAddress";
            break;
          case 6:
            iotMessageFailureDescriptionRes =
              "comBoxSampleIotInfo.socketFailure";
            break;
          case 7:
            iotMessageFailureDescriptionRes =
              "comBoxSampleIotInfo.timeoutIotServerConnect";
            break;
          case 8:
            iotMessageFailureDescriptionRes =
              "comBoxSampleIotInfo.disconnectFailure";
            break;
          case 9:
            iotMessageFailureDescriptionRes =
              "comBoxSampleIotInfo.iotConnectonDisabled";
            break;
        }

        sensorValues.push(
          this.$t("comBoxSampleIotInfo.formatIotInfo", [
            this.$t(iotMessageFailureDescriptionRes).toString(),
            iotMessageFailureCause,
            bc66DriverState,
            nb1DriverState,
            numPowerFailInfo,
          ]).toString()
        );
      } else if (isUartStatusEvent) {
        sensorValues.push("");
        sensorValues.push("");
        sensorValues.push("");
        sensorValues.push("");

        const aggregatedErrorCounter = iresDeviceResponseReader.readUint8();
        const errorCounter: number[] = [];
        for (let i = 0; i < 5; i++) {
          errorCounter.push(iresDeviceResponseReader.readUint8());
        }

        sensorValues.push(
          this.$t("sampleList.formatUartError", [
            aggregatedErrorCounter,
            errorCounter
          ]).toString()
        );
      } else {
        // ==================== FIRMWARE v4.0.0+ CHANGES ====================
        let sensorDataArrayIndex = getSensorDataStartIndexFromSampleFormatVersion(0, runOverview.sampleFormatVersion);

        sensorConfig.forEach((sensorType, sensorIndex) => {
          if (
            sensorType === ComBoxSensorType.EMPTY ||
            sensorType === ComBoxSensorType.UNKNOWN ||
            sensorType === ComBoxSensorType.RADON
          ) {
            sensorValues.push("");
          } else {
            const sensorData = getSensorData(
              sensorType,
              sensorIndex,
              sensorDataArrayIndex,
              data
            );

            if (isBleResistanceSensorType(sensorType)) {
              sensorValues.push(
                this.formatSampleResistanceValue(
                  sensorData.codedResistance,
                  sensorData.resistance
                )
              );
            } else {
              sensorValues.push(
                `${formatTemperature(
                  this,
                  sensorData.temperature
                )} / ${this.formatPercent(sensorData.relativeHumidity)}`
              );
            }

            sensorDataArrayIndex += 2;
          }
        });
      }

      advanceByMinutes(date, timeDiffInMin - previousTimeDiffInMin);

      previousTimeDiffInMin = timeDiffInMin;

      comBoxSampleInfoUpdate.push(filterOutUndefined({
        sampleIndex: sampleIndex,
        guideSensorIndex: guideSensorIndex,
        status: `${deviceState}${relay1state}${relay2state}`,
        event: event,
        time: `${formatFullJsDate(date)} (${this.formatTimeSum(
          timeDiffInMin
        )})`,
        power: runEnergyInWh.toLocaleString(undefined, {
          maximumFractionDigits: 1,
        }),
        step: step,
        cycle: cycle,
        roomSensor: sensorValues[4],
        floorSensor0: sensorValues[0],
        floorSensor1: sensorValues[1],
        floorSensor2: sensorValues[2],
        floorSensor3: sensorValues[3],
        
        // ==================== FIRMWARE v4.0.0+ CHANGES ====================
        floorSensor5: sensorValues[5],
        floorSensor6: sensorValues[6],
        floorSensor7: sensorValues[7],
        floorSensor8: sensorValues[8],

        isSpecialEvent: isSpecialEvent,
      }));
    });

    this.visibleComBoxSensorIndexes = getVisibleComBoxSampleSensorIndexes(
      sensorConfig,
      comBoxSampleInfoUpdate
    );
    this.sampleInfo = comBoxSampleInfoUpdate;
  }

  onWindowsResize() {
    this.tableHeight = window.innerHeight - 180;
  }

  onToggleShowSamplesDescending() {
    this.showSamplesDescending = !this.showSamplesDescending;
  }
}
